import { Skeleton, Typography } from "antd";
import { useSidebar } from "@lib/context/SidebarContext";
import AppCard, { AppCardProps } from "@lib/components/AppComponents/AppCard";
import {
  StyledAppsContainerSidebarUserCardBody,
  StyledAppsContainerSidebarAvatar,
  StyledAppsContainerSidebarCover,
  StyledAppsContainerSidebarUserCardFooter,
} from "./index.styled";
import { useEffect, useRef, useState } from "react";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import AppList, { AppListItemMetaProps } from "../../AppListNew";
import clsx from "clsx";

export type AppsContainerSidebarUserCardProps = AppCardProps & {
  loading?: boolean;
  minWidth?: string;
  maxWidth?: string;
  cardProps?: AppCardProps;
  cover?: React.ReactNode;
  avatar?: React.ReactNode;
  avatarSize?: number;
  header?: {
    title?: string | React.ReactNode;
    extra?: React.ReactNode;
  };
  footer?: React.ReactNode;
  title?: string | React.ReactNode;
  editAction?: () => void;
  titleStyle?: React.CSSProperties;
  listItems?: AppListItemMetaProps[];
};

export default function AppsContainerSidebarUserCard(
  props: AppsContainerSidebarUserCardProps,
) {
  const {
    loading,
    minWidth = "50px",
    maxWidth = "100%",
    cardProps = {
      borderFull: true,
      coloredHeader: true,
    },
    cover,
    avatar,
    avatarSize = 84,
    header,
    footer,
    title,
    editAction,
    titleStyle,
    listItems,
  } = props;
  const { collapsed } = useSidebar();

  const avatarRef = useRef<HTMLDivElement>(null);
  const [avatarHeight, setAvatarHeight] = useState<number>(avatarSize);

  useEffect(() => {
    if (avatarRef?.current) {
      setAvatarHeight(avatarRef.current.clientHeight);
    }
  }, [avatarRef]);

  return (
    <AppCard
      collapsed={collapsed}
      minWidth={minWidth}
      maxWidth={maxWidth}
      {...cardProps}
      {...header}
      className={clsx({ "apps-container-sidebar-user-card": true })}
      style={{
        width: collapsed ? minWidth : maxWidth || "100%",
        maxWidth: collapsed ? minWidth : maxWidth || "100%",
        ...cardProps?.style,
      }}
    >
      <StyledAppsContainerSidebarUserCardBody>
        {cover && (
          <StyledAppsContainerSidebarCover className="cover">
            {cover}
          </StyledAppsContainerSidebarCover>
        )}

        {avatar && (
          <StyledAppsContainerSidebarAvatar
            className="avatar"
            ref={avatarRef}
            style={{
              marginTop: cover ? `-${avatarHeight / 2}px` : 0,
              cursor: editAction ? "pointer" : "default",
            }}
            onClick={() => editAction?.()}
            avatarSize={avatarSize}
          >
            {loading ? (
              <Skeleton.Node active style={{ borderRadius: "50%" }} />
            ) : (
              avatar
            )}
          </StyledAppsContainerSidebarAvatar>
        )}

        <Typography.Title
          level={5}
          style={{
            fontSize: 20,
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 0,
            ...titleStyle,
            cursor: editAction ? "pointer" : "default",
          }}
          onClick={() => editAction?.()}
        >
          {loading ? <Skeleton.Input active /> : title}

          {editAction && !loading && (
            <ActionButton
              type="edit"
              noLabel
              noBg={loading}
              size="small"
              onClick={() => editAction()}
            />
          )}
        </Typography.Title>

        {listItems && (
          <AppList
            dataSource={listItems}
            padding="10px 20px"
            renderItem={(item, index) => (
              <AppList.Item.Meta key={index} {...item} />
            )}
            loading={loading}
          />
        )}
      </StyledAppsContainerSidebarUserCardBody>

      {footer && (
        <StyledAppsContainerSidebarUserCardFooter>
          {footer}
        </StyledAppsContainerSidebarUserCardFooter>
      )}
    </AppCard>
  );
}
